import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { 
  FireIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  StarIcon,
  PlayIcon,
  PauseIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const WorkoutPlansList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Mock data - à remplacer par une vraie API
  const mockPlans = [
    {
      id: 1,
      name: 'Programme Débutant',
      description: 'Plan d\'entraînement idéal pour commencer la musculation',
      difficulty: 'beginner',
      duration: 4,
      sessions: 3,
      members: 15,
      status: 'active',
      exercises: 12,
      category: 'Musculation',
      author: 'Coach Ahmed',
      createdAt: '2024-01-15',
      image: null
    },
    {
      id: 2,
      name: 'Force et Masse',
      description: 'Programme avancé pour développer la force et la masse musculaire',
      difficulty: 'advanced',
      duration: 8,
      sessions: 4,
      members: 8,
      status: 'active',
      exercises: 18,
      category: 'Musculation',
      author: 'Coach Fatima',
      createdAt: '2024-01-10',
      image: null
    },
    {
      id: 3,
      name: 'Cardio Intensif',
      description: 'Entraînement cardiovasculaire pour la perte de poids',
      difficulty: 'intermediate',
      duration: 6,
      sessions: 5,
      members: 22,
      status: 'active',
      exercises: 10,
      category: 'Cardio',
      author: 'Coach Omar',
      createdAt: '2024-01-08',
      image: null
    },
    {
      id: 4,
      name: 'Functional Training',
      description: 'Mouvements fonctionnels pour la vie quotidienne',
      difficulty: 'intermediate',
      duration: 4,
      sessions: 3,
      members: 12,
      status: 'draft',
      exercises: 15,
      category: 'Fonctionnel',
      author: 'Coach Sarah',
      createdAt: '2024-01-05',
      image: null
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 text-green-400';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-400';
      case 'advanced': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'Débutant';
      case 'intermediate': return 'Intermédiaire';
      case 'advanced': return 'Avancé';
      default: return 'Non défini';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'draft': return 'bg-yellow-500/20 text-yellow-400';
      case 'archived': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'draft': return 'Brouillon';
      case 'archived': return 'Archivé';
      default: return 'Inconnu';
    }
  };

  const filteredPlans = mockPlans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plan.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plan.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = difficultyFilter === 'all' || plan.difficulty === difficultyFilter;
    const matchesStatus = statusFilter === 'all' || plan.status === statusFilter;
    
    return matchesSearch && matchesDifficulty && matchesStatus;
  });

  const handleDelete = (planId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce plan d\'entraînement ?')) {
      // Logique de suppression
      console.log('Suppression du plan:', planId);
    }
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setEditMode(true);
    console.log('Modification du plan:', plan.id);
  };

  return (
    <div className="space-y-6">
      {/* Filtres et recherche */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        {/* Recherche */}
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
          <input
            type="text"
            placeholder="Rechercher un plan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        {/* Filtres */}
        <div className="flex items-center gap-3">
          <FunnelIcon className="h-5 w-5 text-white/50" />
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Tous niveaux</option>
            <option value="beginner">Débutant</option>
            <option value="intermediate">Intermédiaire</option>
            <option value="advanced">Avancé</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Tous statuts</option>
            <option value="active">Actifs</option>
            <option value="draft">Brouillons</option>
            <option value="archived">Archivés</option>
          </select>
        </div>
      </div>

      {/* Liste des plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlans.map((plan, index) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden hover:bg-white/15 transition-all duration-300 group"
          >
            {/* Image d'en-tête */}
            <div className="h-40 bg-gradient-to-br from-orange-500/20 to-red-500/20 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute top-4 right-4 flex gap-2">
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getDifficultyColor(plan.difficulty)}`}>
                  {getDifficultyText(plan.difficulty)}
                </span>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(plan.status)}`}>
                  {getStatusText(plan.status)}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 text-white">
                <div className="flex items-center gap-2 text-sm">
                  <FireIcon className="h-4 w-4" />
                  <span>{plan.category}</span>
                </div>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-white group-hover:text-orange-300 transition-colors">
                  {plan.name}
                </h3>
                <div className="flex items-center gap-1">
                  <StarIcon className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="text-white/60 text-sm">4.8</span>
                </div>
              </div>

              <p className="text-white/60 text-sm mb-4 line-clamp-2">
                {plan.description}
              </p>

              {/* Statistiques */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-white font-semibold">{plan.duration}</div>
                  <div className="text-white/60 text-xs">semaines</div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold">{plan.sessions}</div>
                  <div className="text-white/60 text-xs">séances/sem</div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold">{plan.exercises}</div>
                  <div className="text-white/60 text-xs">exercices</div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold">{plan.members}</div>
                  <div className="text-white/60 text-xs">membres</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="text-white/50 text-xs">
                  Par {plan.author}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedPlan(plan)}
                    className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                    title="Voir les détails"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(plan)}
                    className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                    title="Modifier"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                    title="Supprimer"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* État vide */}
      {filteredPlans.length === 0 && (
        <div className="text-center py-12">
          <FireIcon className="h-16 w-16 text-white/20 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white/60 mb-2">
            Aucun plan trouvé
          </h3>
          <p className="text-white/40">
            {searchTerm || difficultyFilter !== 'all' || statusFilter !== 'all'
              ? 'Essayez de modifier vos critères de recherche'
              : 'Commencez par créer votre premier plan d\'entraînement'
            }
          </p>
        </div>
      )}

      {/* Modal de détails du plan */}
      {selectedPlan && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPlan(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">{selectedPlan.name}</h2>
              <button
                onClick={() => setSelectedPlan(null)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Informations générales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <ClockIcon className="h-6 w-6 text-orange-400 mx-auto mb-2" />
                  <div className="text-xl font-bold text-white">{selectedPlan.duration}</div>
                  <div className="text-white/60 text-sm">semaines</div>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <CalendarDaysIcon className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                  <div className="text-xl font-bold text-white">{selectedPlan.sessions}</div>
                  <div className="text-white/60 text-sm">séances/sem</div>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <FireIcon className="h-6 w-6 text-red-400 mx-auto mb-2" />
                  <div className="text-xl font-bold text-white">{selectedPlan.exercises}</div>
                  <div className="text-white/60 text-sm">exercices</div>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <UserGroupIcon className="h-6 w-6 text-green-400 mx-auto mb-2" />
                  <div className="text-xl font-bold text-white">{selectedPlan.members}</div>
                  <div className="text-white/60 text-sm">membres</div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Description</h3>
                <p className="text-white/80">{selectedPlan.description}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    console.log('Assigner le plan aux membres:', selectedPlan.id);
                    // Cette fonction simulerait l'ouverture d'une modal d'assignation
                    toast.success(`Plan ${selectedPlan.name} prêt pour l'assignation aux membres`);
                    // On fermerait la modal de détail et ouvrirait une nouvelle modal d'assignation
                    setSelectedPlan(null);
                    // Ici on pourrait mettre un state comme setShowAssignModal(true);
                  }}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all">
                  Assigner aux membres
                </button>
                <button 
                  onClick={() => {
                    handleEdit(selectedPlan);
                    setSelectedPlan(null);
                  }}
                  className="flex-1 bg-white/10 text-white px-4 py-2 rounded-xl hover:bg-white/20 transition-all border border-white/20">
                  Modifier le plan
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default WorkoutPlansList; 